import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";

import { ReactNode } from "react";
import { makeStyles } from "@mui/styles";
import { globalColors } from "../../../Utils/colors";
import { Button, styled, useMediaQuery } from "@mui/material";
import { ButtonProps as MuiButtonProps } from "@mui/material/Button";

import {
  AddIcon,
  DeleteForeverIcon,
  DeleteIcon,
  OpenFormIcon,
  PreviewIcon,
  ShareIcon,
  PublishIcon,
  SaveIcon,
  SettingsIcon,
  UnPublishIcon,
} from "./ButtonIcons";

interface SelectableButtonProps {
  iconRenderer: () => ReactNode;
  onClick: () => void;
  isSelected: boolean;
  title: string;
}

const useStyles = makeStyles({
  settingsButtonStyle: {
    height: "27px",
  },
  deleteButtonStyle: {
    height: "27px",
  },
  exitButtonStyle: {
    height: "34px",
  },
  actionButtonStyle: {
    height: "34px",
    width: "160px",
  },
  actionButtonStyleSM: {
    height: "34px",
    width: "unset",
  },
  createNewButtonStyle: {
    height: "40px",
    textWrap: "nowrap",
  },
  continueButtonStyle: {
    padding: "15px 35px",
    fontSize: "16px",
    width: "200px",
  },
  copyLinkButtonStyle: {
    padding: "8px",
    fontSize: "16px",
    width: "200px",
  },
});

interface CustomButtonProps extends MuiButtonProps {
  text: string
}

export const OutlinedButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "backgroundColor" && prop !== "textColor",
})<{
  backgroundColor?: string;
  textColor?: string;
}>(({ backgroundColor, textColor }) => ({
  borderRadius: "5px",
  borderColor: backgroundColor,
  color: textColor || backgroundColor,
  fontSize: "12px",
  fontFamily: "Poppins",
  textTransform: "none",
  marginRight: "5px",
  "&:hover": {
    borderColor: textColor || backgroundColor,
    backdropClasses: "#ffff",
  },
}));

const ContainedButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "backgroundColor" && prop !== "textColor",
})<{
  backgroundColor?: string;
  textColor?: string;
}>(({ backgroundColor, textColor }) => ({
  backgroundColor: backgroundColor,
  borderRadius: "5px",
  color: textColor || globalColors.white,
  fontSize: "10px",
  fontFamily: "Poppins",
  textTransform: "none",
  marginRight: "5px",
  "&:hover": {
    backgroundColor: backgroundColor,
  },
}));

interface publishBtnProps extends MuiButtonProps {
  isPublished: boolean;
}

export const LogicCancelButton = (props: MuiButtonProps) => {
  return (
    <OutlinedButton
      variant="outlined"
      backgroundColor={globalColors.primary}
      sx={{ height: "30px" }}
      {...props}
    >
      Cancel
    </OutlinedButton>
  );
};

export const CustomButton = (props: CustomButtonProps) => {
  return (
    <OutlinedButton
      variant="outlined"
      backgroundColor={globalColors.primary}
      style={{ textWrap: "nowrap" }}
      sx={{ height: "30px" }}
      {...props}
    >
      {props.text}
    </OutlinedButton>
  );
};

export const LogicSaveButton = (props: MuiButtonProps) => {
  return (
    <ContainedButton
      variant="contained"
      backgroundColor={globalColors.primary}
      sx={{ height: "30px" }}
      {...props}
    >
      Save
    </ContainedButton>
  );
};

export const LogicsButton = (props: MuiButtonProps) => {
  const buttonText = "Logics";

  return (
    <OutlinedButton
      variant="outlined"
      backgroundColor={globalColors.primary}
      endIcon={<NavigateNextIcon />}
      sx={{ height: "25px", marginLeft: "15px" }}
      {...props}
    >
      {buttonText}
    </OutlinedButton>
  );
};

export const LogicAddRuleButton = (props: MuiButtonProps) => {
  return (
    <OutlinedButton
      variant="outlined"
      backgroundColor={globalColors.primary}
      {...props}
    >
      Add Rule
    </OutlinedButton>
  );
};

export const LogicAddConditionButton = (props: MuiButtonProps) => {
  return (
    <OutlinedButton
      variant="outlined"
      backgroundColor={globalColors.primary}
      {...props}
    >
      +Add Condition
    </OutlinedButton>
  );
};

export const LogicRuleButton = (props: MuiButtonProps) => {
  return (
    <OutlinedButton
      variant="outlined"
      backgroundColor={globalColors.primary}
      {...props}
    >
      Add Rule
    </OutlinedButton>
  );
};

export const LogicDeleteConditionButton = (props: MuiButtonProps) => {
  return (
    <OutlinedButton
      variant="outlined"
      backgroundColor={globalColors.primary}
      {...props}
    >
      Delete Condition
    </OutlinedButton>
  );
};

export const LogicDeleteRuleButton = (props: MuiButtonProps) => {
  return (
    <OutlinedButton
      variant="outlined"
      backgroundColor={globalColors.primary}
      {...props}
    >
      Delete Rule
    </OutlinedButton>
  );
};

export const AdvancedSettingsButton = (props: MuiButtonProps) => {
  const isLessThan1280 = useMediaQuery("(max-width:1279px)");
  const buttonText = isLessThan1280 ? "Advanced" : "Advanced settings";

  return (
    <OutlinedButton
      variant="outlined"
      backgroundColor={globalColors.primary}
      endIcon={<NavigateNextIcon />}
      sx={{ height: "30px", marginLeft: "15px" }}
      {...props}
    >
      {buttonText}
    </OutlinedButton>
  );
};

export const BackToBasicSettingsButton = (props: MuiButtonProps) => {
  const isLessThan1280 = useMediaQuery("(max-width:1279px)");
  const buttonText = isLessThan1280 ? "Basic" : "Back to basic settings";

  return (
    <OutlinedButton
      variant="outlined"
      backgroundColor={globalColors.primary}
      startIcon={<NavigateBeforeIcon />}
      sx={{ height: "30px", marginLeft: "15px" }}
      {...props}
    >
      {buttonText}
    </OutlinedButton>
  );
};

export const PrimaryButton = (props: MuiButtonProps) => {
  return (
    <ContainedButton
      variant="contained"
      backgroundColor={globalColors.primary}
      {...props}
    >
      {props.children}
    </ContainedButton>
  );
};

export const SecondaryButton = (props: MuiButtonProps) => {
  return (
    <OutlinedButton
      variant="outlined"
      backgroundColor={globalColors.primary}
      {...props}
    >
      {props.children}
    </OutlinedButton>
  );
};

export const SettingsButton = ({ disabled, onClick }: MuiButtonProps) => {
  const classes = useStyles();

  return (
    <OutlinedButton
      backgroundColor={globalColors.primary}
      className={classes.settingsButtonStyle}
      disabled={disabled}
      onClick={onClick}
      size="large"
      startIcon={<SettingsIcon disabled={disabled || false} />}
      variant="outlined"
    >
      Settings
    </OutlinedButton>
  );
};

export const DeleteButton = ({ disabled, onClick }: MuiButtonProps) => {
  const classes = useStyles();

  return (
    <OutlinedButton
      backgroundColor={globalColors.primary}
      className={classes.deleteButtonStyle}
      disabled={disabled}
      onClick={onClick}
      startIcon={<DeleteIcon disabled={disabled || false} />}
      variant="outlined"
      size="large"
    >
      Delete
    </OutlinedButton>
  );
};

export const ExitButton = (props: MuiButtonProps) => {
  const classes = useStyles();

  return (
    <OutlinedButton
      className={classes.exitButtonStyle}
      backgroundColor={globalColors.primary}
      disabled={props.disabled}
      onClick={props.onClick}
      startIcon={<ExitToAppIcon />}
      variant="outlined"
      size="large"
    >
      Exit
    </OutlinedButton>
  );
};

export const FormLinkButton = ({ disabled, onClick }: MuiButtonProps) => {
  const classes = useStyles();
  const isLessThan1600 = useMediaQuery("(max-width:1600px)");

  const selectedClassName = isLessThan1600
    ? classes.actionButtonStyleSM
    : classes.actionButtonStyle;

  return (
    <ContainedButton
      backgroundColor={globalColors.openForm}
      className={selectedClassName}
      disabled={disabled}
      onClick={onClick}
      size="large"
      startIcon={<OpenFormIcon disabled={disabled || false} />}
      variant="contained"
    >
      Open Form
    </ContainedButton>
  );
};

export const PreviewButton = ({ disabled, onClick }: MuiButtonProps) => {
  const classes = useStyles();
  const isLessThan1600 = useMediaQuery("(max-width:1600px)");

  const selectedClassName = isLessThan1600
    ? classes.actionButtonStyleSM
    : classes.actionButtonStyle;

  return (
    <ContainedButton
      backgroundColor={globalColors.preview}
      className={selectedClassName}
      disabled={disabled}
      onClick={onClick}
      size="large"
      startIcon={<PreviewIcon disabled={disabled || false} />}
      variant="contained"
    >
      Preview
    </ContainedButton>
  );
};

export const ShareButton = ({ disabled, onClick }: MuiButtonProps) => {
  const classes = useStyles();
  const isLessThan1600 = useMediaQuery("(max-width:1600px)");

  const selectedClassName = isLessThan1600
    ? classes.actionButtonStyleSM
    : classes.actionButtonStyle;

  return (
    <ContainedButton
      backgroundColor={globalColors.share}
      className={selectedClassName}
      disabled={disabled}
      onClick={onClick}
      size="large"
      startIcon={<ShareIcon disabled={disabled || false} />}
      variant="contained"
    >
      Share
    </ContainedButton>
  );
};
export const SaveChangesButton = ({ disabled, onClick }: MuiButtonProps) => {
  const classes = useStyles();
  const isLessThan1600 = useMediaQuery("(max-width:1600px)");

  const selectedClassName = isLessThan1600
    ? classes.actionButtonStyleSM
    : classes.actionButtonStyle;

  return (
    <ContainedButton
      backgroundColor={globalColors.saveChanges}
      className={selectedClassName}
      disabled={disabled}
      onClick={onClick}
      size="large"
      startIcon={<SaveIcon disabled={disabled || false} />}
      variant="contained"
    >
      Save
    </ContainedButton>
  );
};

export const PublishButton = ({
  isPublished,
  disabled,
  onClick,
}: publishBtnProps) => {
  const classes = useStyles();
  const buttonText = isPublished ? "Unpublish" : "Publish";
  const isLessThan1600 = useMediaQuery("(max-width:1600px)");
  const selectedClassName = isLessThan1600
    ? classes.actionButtonStyleSM
    : classes.actionButtonStyle;

  const startIcon = isPublished ? (
    <UnPublishIcon disabled={disabled || false} />
  ) : (
    <PublishIcon disabled={disabled || false} />
  );

  return (
    <ContainedButton
      backgroundColor={globalColors.primary}
      className={selectedClassName}
      disabled={disabled}
      onClick={onClick}
      size="large"
      startIcon={startIcon}
      variant="contained"
    >
      {buttonText}
    </ContainedButton>
  );
};

export const ClearButton = (props: MuiButtonProps) => {
  return (
    <OutlinedButton
      sx={{ marginLeft: "20px" }}
      backgroundColor={globalColors.primary}
      onClick={props.onClick}
      variant="outlined"
      size="small"
    >
      Clear
    </OutlinedButton>
  );
};

export const ChooseExistingTemplateButton = (props: MuiButtonProps) => {
  return (
    <ContainedButton
      backgroundColor={globalColors.primary}
      disabled={props.disabled}
      onClick={props.onClick}
      variant="contained"
      size="small"
    >
      Choose Existing Template
    </ContainedButton>
  );
};

export const SelectButton = (props: MuiButtonProps) => {
  return (
    <ContainedButton
      backgroundColor={globalColors.primary}
      disabled={props.disabled}
      onClick={props.onClick}
      variant="contained"
      size="small"
    >
      Select
    </ContainedButton>
  );
};

export const CreateNewFormButton = ({ disabled, onClick }: MuiButtonProps) => {
  const classes = useStyles();
  const isLessThan1280 = useMediaQuery("(max-width:1260px)");

  return (
    <ContainedButton
      backgroundColor={globalColors.primary}
      className={classes.createNewButtonStyle}
      disabled={disabled}
      endIcon={isLessThan1280 ? null : <AddIcon disabled={disabled || false} />}
      onClick={onClick}
      size="large"
      variant="contained"
    >
      Create new Form
    </ContainedButton>
  );
};

export const TakeToTemplatesButton = (props: MuiButtonProps) => {
  const classes = useStyles();
  const isLessThan1280 = useMediaQuery("(max-width:1260px)");

  return (
    <ContainedButton
      className={classes.createNewButtonStyle}
      backgroundColor={globalColors.primary}
      disabled={props.disabled}
      onClick={props.onClick}
      variant="contained"
      size="large"
      endIcon={isLessThan1280 ? null : <ArrowRightIcon />}
    >
      Take me back to my templates
    </ContainedButton>
  );
};

export const DeleteForeverButton = ({ disabled, onClick }: MuiButtonProps) => {
  const classes = useStyles();
  const isLessThan1280 = useMediaQuery("(max-width:1260px)");

  return (
    <ContainedButton
      backgroundColor={globalColors.errorColor}
      className={classes.createNewButtonStyle}
      disabled={disabled}
      endIcon={
        isLessThan1280 ? null : (
          <DeleteForeverIcon disabled={disabled || false} />
        )
      }
      onClick={onClick}
      size="large"
      variant="contained"
    >
      Delete All Forever
    </ContainedButton>
  );
};

export const ChooseYourFileButton = ({ disabled, onClick }: MuiButtonProps) => {
  return (
    <ContainedButton
      backgroundColor={globalColors.primary}
      disabled={disabled}
      endIcon={<AddIcon disabled={disabled || false} />}
      onClick={onClick}
      size="small"
      variant="contained"
      className="file-upload-button"
    >
      Choose Your File
    </ContainedButton>
  );
};

export const EditButton = (props: MuiButtonProps) => {
  return (
    <ContainedButton
      backgroundColor={globalColors.primary}
      disabled={props.disabled}
      onClick={props.onClick}
      variant="contained"
      size="small"
    >
      Edit
    </ContainedButton>
  );
};

export const ContinueButton = (props: MuiButtonProps) => {
  const classes = useStyles();

  return (
    <ContainedButton
      className={classes.continueButtonStyle}
      backgroundColor={globalColors.primary}
      disabled={props.disabled}
      onClick={props.onClick}
      variant="contained"
      size="large"
    >
      Continue
    </ContainedButton>
  );
};

export const CopyLinkButton = (props: MuiButtonProps) => {
  const classes = useStyles();

  return (
    <ContainedButton
      className={classes.copyLinkButtonStyle}
      backgroundColor={globalColors.primary}
      disabled={props.disabled}
      onClick={props.onClick}
      startIcon={<ContentCopyIcon />}
      variant="contained"
    >
      Copy Link
    </ContainedButton>
  );
};

export const SelectableButton = ({
  iconRenderer,
  isSelected,
  onClick,
  title,
}: SelectableButtonProps) => {
  return isSelected ? (
    <ContainedButton
      disableRipple
      sx={{ height: 30, border: `1px solid ${globalColors.primary}` }}
      backgroundColor={globalColors.primary}
      textColor={globalColors.white}
      onClick={onClick}
      startIcon={iconRenderer()}
      variant="contained"
    >
      {title}
    </ContainedButton>
  ) : (
    <OutlinedButton
      disableRipple
      sx={{ height: 30, border: `1px solid ${globalColors.primary}` }}
      backgroundColor={globalColors.white}
      textColor={globalColors.primary}
      onClick={onClick}
      startIcon={iconRenderer()}
      variant="outlined"
    >
      {title}
    </OutlinedButton>
  );
};

export const AddGroupButton = ({ disabled, onClick }: MuiButtonProps) => {
  return (
    <ContainedButton
      backgroundColor={globalColors.primary}
      disabled={disabled}
      onClick={onClick}
      startIcon={<AddIcon disabled={disabled || false} />}
      variant="contained"
      size="small"
    >
      Add Group
    </ContainedButton>
  );
};
